import type { FocusEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useFieldContext } from '../../contexts';

type UseFieldOptions<T> = {
  updateOnBlur?: boolean;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: ((val: T) => void) | undefined;
  // Transform the value on every change before it is displayed and committed to
  // form state, e.g. to force-uppercase an identifier as the user types. Applied
  // once, so the input never momentarily shows the untransformed value.
  format?: ((val: T) => T) | undefined;
};

export function useField<T>({ updateOnBlur, onBlur: onBlurOpt, onChange: onChangeOpt, format }: UseFieldOptions<T>) {
  const field = useFieldContext<T>();
  const isInvalid = field.state.meta.isTouched && field.state.meta.isDirty && field.state.meta.errors.length > 0;
  const [value, setValue] = useState(field.state.value);

  useEffect(() => {
    setValue(field.state.value);
  }, [field.state.value]);

  const onBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      field.handleBlur();
      if (updateOnBlur) {
        field.handleChange(value);
      }
      onBlurOpt?.(e);
    },
    [field, updateOnBlur, onBlurOpt, value],
  );

  const onChange = useCallback(
    (val: T) => {
      const next = format ? format(val) : val;
      setValue(next);
      if (!updateOnBlur) {
        field.handleChange(next);
      }
      onChangeOpt?.(next);
    },
    [updateOnBlur, onChangeOpt, field, format],
  );

  return { field, onBlur, onChange, value, isInvalid };
}
